import _ from 'lodash';
import {getSchemaOfAnimation, animationOptionsMappings, EASING_MAP} from './animationSchemas';
import {validateByType, isValueInRange, isValueInPattern} from '../validation';
import reporter from '../../reporter';
import {generateError} from '../errors';

function reportInvalidOptionsValue(value, key, compNickname) {
    reporter.reportSdkError(generateError.error_invalid_animation_options({
        compNickname,
        wrongProperty: 'value',
        wrongValue: JSON.stringify({[key]: value})
    }));
}

function reportInvalidKeys(invalidKeys, compNickname) {
    if (!_.isEmpty(invalidKeys)) {
        reporter.reportSdkError(generateError.error_invalid_animation_options({
            compNickname,
            wrongProperty: 'keys',
            wrongValue: invalidKeys
        }));
        return false;
    }
    return true;
}

function reportInvalidAnimationName(animationName, compNickname) {
    reporter.reportSdkError(generateError.error_invalid_animation_name({
        compNickname,
        wrongValue: animationName
    }));
}
/**
 * 
 * @param value 
 * @param key 
 * @param schema 
 * @returns {boolean}
 */
function validateSingleParam(value, key, schema) {
    // If oneOf, run through schemas and pass if one schema passes.
    if (schema.oneOf) {
        return _.some(schema.oneOf, (oneOfSchema) => validateSingleParam(value, key, oneOfSchema));
    } 
    
    // If type mismatch, fail
    if (schema.type && !validateByType({value, expectedType: schema.type})) {
        return false;
    } 
    
    // Test value validity
    let valid = true;
    if (schema.enum) {
        valid = _.includes(schema.enum, value);
    } else if (schema.pattern) {
        valid = isValueInPattern({value, pattern: schema.pattern});
    } else if (schema.range) {
        const {minValue = Number.MIN_SAFE_INTEGER, maxValue = Number.MAX_SAFE_INTEGER} = schema.range;
        valid = isValueInRange({value, minValue, maxValue});
    }

    return valid;
}

/**
 * @deprecated
 * @param {object} animationOptions 
 * @param {object} schemaValidations 
 * @param {string} compNickname 
 * @param {boolean} options.shouldReport
 */
function validateAnimationOptionsAndReportIfNecessary(animationOptions, schemaValidations, compNickname) {
    let valid = true;
    _.forEach(animationOptions, (value, key) => {
        const schema = schemaValidations[key];
        valid = validateSingleParam(value, key, schema);
        if (!valid) {
            reportInvalidOptionsValue(value, key, compNickname);
        }
    });
    return valid;
}

/**
 * @deprecated
 */
function validateSequence(animations, sequenceOptions, compNickname) {
    if (!_.isArray(animations)) {
        reporter.reportSdkError(generateError.error_invalid_animation_type({
            compNickname,
            wrongValue: JSON.stringify(animations)
        }));
        return false;
    }

    const isTopLevelValid = validateAnimation('sequence', sequenceOptions, compNickname);
    const isAnimationsListValid = _.every(animations, (animation) => {
            const {name, ...options} = animation ? animation : {};
            return validateAnimation(name, options, compNickname);
        });
    return isTopLevelValid && isAnimationsListValid;
}

function validateAnimation(animationName, animationOptions, compNickname) {
    const animationSchema = getSchemaOfAnimation(animationName);
    if (!animationSchema) {
        reportInvalidAnimationName(animationName, compNickname);
        return false;
    }

    const invalidKeys = _.difference(_.keys(animationOptions), _.keys(animationSchema.validations));
    const allOptionKeysValid = reportInvalidKeys(invalidKeys, compNickname);

    if (!allOptionKeysValid) {
        return false;
    }

    return validateAnimationOptionsAndReportIfNecessary(animationOptions, animationSchema.validations, compNickname);
}

/**
 * 
 * @param {string} animationName 
 * @param {object} animationParams 
 * @param {string} compNickname 
 */
function filterInvalidAnimationParams(animationName, animationParams, compNickname) {
    const animationSchema = getSchemaOfAnimation(animationName);
    // If no schema than this animation name is not supported, report
    if (!animationSchema) {
        reportInvalidAnimationName(animationName, compNickname);
        return {};
    }
    
    // check if passed animation params are valid, report invalid
    const invalidKeys = _.difference(_.keys(animationParams), _.keys(animationSchema.validations));
    reportInvalidKeys(invalidKeys, compNickname);

    // Omit invalid params
    const validParams = _.omit(animationParams, invalidKeys);

    // Pick valid params
    return _.pickBy(validParams, (value, key) => {
        const schema = animationSchema.validations[key];
        const valid = validateSingleParam(value, key, schema);
        if (!valid) {
            reportInvalidOptionsValue(value, key, compNickname);
        }
        return valid;
    });
}
/**
 * convert number of '+=<number>' from ms to sec
 * @param {string|number} value
 * @returns {string|number}
 */
function convertNumberLikeMsToSecs(value) {
    if (_.isNumber(value)) {
        return value / 1000;
    }
    return value.replace(/\d+/, num => +num / 1000);
}

/**
 * Build animation params for viewer from schema transformation definitions
 * @param {string} animationName 
 * @param {object} animationOptions 
 */
function buildAnimationParams(animationName, animationOptions) {
    const animationSchema = getSchemaOfAnimation(animationName);

    const params = _.reduce(animationOptions, (acc, value, key) => {
        const path = _.get(animationOptionsMappings, ['keys', key], key);
        const newValue = _.get(animationOptionsMappings, ['values', key, 'convertMsToSecs']) ? convertNumberLikeMsToSecs(value) : value;
        // using set to convert strings in the form of 'to.x' to and object path
        _.set(acc, path, newValue);
        return acc;
    }, {...animationSchema.default});

    if (params.ease) {
        params.ease = EASING_MAP[params.ease];
    }

    return params;
}

export default {
    validateAnimation,
    validateSequence,
    buildAnimationParams,
    filterInvalidAnimationParams
};

